import React from 'react';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';
import type { ChartDefinition } from '@/pages/analytics/overview/registry';

export interface ChartDefinitionDrawerProps {
  open: boolean;
  onClose: () => void;
  chart: ChartDefinition | null;
}

function Section({
  title,
  items,
}: {
  title: string;
  items: string[];
}) {
  if (!items?.length) return null;
  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-foreground">{title}</h4>
      <ul className="list-disc list-inside space-y-1 text-sm text-muted-foreground">
        {items.map((item, i) => (
          <li key={i}>{item}</li>
        ))}
      </ul>
    </div>
  );
}

export const ChartDefinitionDrawer: React.FC<ChartDefinitionDrawerProps> = ({
  open,
  onClose,
  chart,
}) => {
  const method = chart?.method;

  return (
    <Sheet open={open} onOpenChange={(open) => !open && onClose()}>
      <SheetContent
        side="right"
        className="w-[min(480px,92vw)] overflow-y-auto flex flex-col"
      >
        <SheetHeader>
          <SheetTitle>{chart?.title ?? 'Chart definition'}</SheetTitle>
          {chart?.description && (
            <SheetDescription>{chart.description}</SheetDescription>
          )}
        </SheetHeader>
        <div className="flex-1 pt-4 space-y-6">
          {!method && (
            <p className="text-sm text-muted-foreground">
              No definition available for this chart.
            </p>
          )}
          {method && (
            <>
              <Section title="What it measures" items={[method.what]} />
              <Section title="Calculation" items={method.calculation} />
              <Section title="Thresholds used" items={method.thresholds ?? []} />
              <Section title="Caveats" items={method.caveats ?? []} />
              <Section title="Drill-through behaviour" items={method.drillthrough} />
              <Section title="Data inputs" items={method.inputs} />
            </>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};
